/**
 * useTheme — zarządzanie motywem wizualnym e-grób
 * Persystencja: localStorage ("egrob_theme")
 * Tokeny CSS aplikowane na <html> jako CSS custom properties
 */

export type ThemeId = 'classic' | 'graphite' | 'soft';

export interface ThemePreset {
  id: ThemeId;
  label: string;
  description: string;
  accentColor: string;    // do podglądu w UI (ThemeSwitcher dot)
  statusBarColor: string; // kolor meta theme-color (pasek statusu telefonu)
  tokens: Record<string, string>;
}

/* ── Definicje obu motywów ── */
export const THEMES: ThemePreset[] = [
  {
    id: 'classic',
    label: 'Klasyczne Złoto',
    description: 'Oryginalny motyw — granatowa czerń i ciepłe złoto',
    accentColor: '#d4af37',
    statusBarColor: '#0a0c18',
    tokens: {
      '--background':           'oklch(0.20 0.018 237)',
      '--foreground':           'oklch(0.96 0.008 45)',
      '--card':                 'oklch(0.29 0.022 230)',
      '--card-foreground':      'oklch(0.94 0.01 45)',
      '--popover':              'oklch(0.28 0.020 232)',
      '--popover-foreground':   'oklch(0.96 0.008 45)',
      '--primary':              'oklch(0.72 0.14 45)',
      '--primary-foreground':   'oklch(0.08 0.01 240)',
      '--secondary':            'oklch(0.27 0.025 225)',
      '--secondary-foreground': 'oklch(0.94 0.01 45)',
      '--muted':                'oklch(0.25 0.022 228)',
      '--muted-foreground':     'oklch(0.62 0.025 210)',
      '--accent':               'oklch(0.76 0.16 45)',
      '--accent-foreground':    'oklch(0.08 0.01 240)',
      '--destructive':          'oklch(0.58 0.20 15)',
      '--border':               'oklch(0.34 0.025 230)',
      '--input':                'oklch(0.30 0.025 228)',
      '--ring':                 'oklch(0.72 0.14 45)',
      '--gold':                 'oklch(0.72 0.14 45)',
      '--gold-bright':          'oklch(0.82 0.16 50)',
      '--navy':                 'oklch(0.12 0.04 230)',
      '--navy-deep':            'oklch(0.08 0.03 230)',
      // niestandardowe tokeny motywu
      '--theme-hero-opacity':   '0.55',
      '--theme-btn-glow':       'rgba(212,175,55,0.18)',
      '--theme-btn-glow-hover': 'rgba(212,175,55,0.32)',
      '--theme-accent-rgb':     '212,175,55',
      '--theme-skeleton':       '0',
      '--theme-panel-bg':           'oklch(0.09 0.018 225)',
      '--theme-panel-border':       'oklch(0.20 0.035 220)',
      '--theme-panel-header-bg':    'oklch(0.075 0.014 225)',
      '--theme-panel-inner-border': 'oklch(0.14 0.022 220)',
      '--theme-section-heading':    'rgba(212,175,55,0.72)',
      '--theme-text-bright':        'rgba(225,215,195,0.92)',
      '--theme-text-muted':         'rgba(145,160,180,0.72)',
      '--theme-text-dim':           'rgba(120,135,155,0.55)',
      '--theme-card-bg-deep':       'oklch(0.10 0.02 220)',
      '--theme-card-bg-medium':     'oklch(0.13 0.025 220)',
      '--theme-visits-bg':          'rgba(6,8,18,0.78)',
      '--theme-visits-border':      'rgba(212,175,55,0.16)',
      '--theme-visits-accent':      'rgba(212,175,55,0.9)',
      '--theme-banner-bg':          'rgba(6,8,18,0.88)',
      '--theme-banner-border':      'rgba(212,175,55,0.22)',
      '--theme-footer-bg':          'oklch(0.06 0.01 240)',
      '--theme-footer-card-bg':     'oklch(0.085 0.018 228)',
      '--theme-footer-card-border': 'oklch(0.16 0.025 220)',
      '--theme-footer-help-bg':     'oklch(0.08 0.016 228)',
      '--theme-help-color':         'rgba(100,205,150,0.75)',
      '--theme-help-bg':            'rgba(100,180,140,0.08)',
      '--theme-help-border':        'rgba(100,180,140,0.18)',
    },
  },
  {
    id: 'soft',
    label: 'Mglisty Świt',
    description: 'Ciemna głębia, mglisty świt, ognisty akcent',
    accentColor: '#e8874a',
    statusBarColor: '#160a04',
    tokens: {
      // ── Tła — głęboka ciemna czerwień/brąz jak godzina po zachodzie słońca
      '--background':           'oklch(0.14 0.028 32)',
      '--foreground':           'oklch(0.93 0.012 55)',
      '--card':                 'oklch(0.18 0.030 30)',
      '--card-foreground':      'oklch(0.91 0.010 50)',
      '--popover':              'oklch(0.17 0.028 30)',
      '--popover-foreground':   'oklch(0.93 0.012 55)',
      '--primary':              'oklch(0.68 0.16 40)',
      '--primary-foreground':   'oklch(0.06 0.010 30)',
      '--secondary':            'oklch(0.20 0.032 28)',
      '--secondary-foreground': 'oklch(0.88 0.010 50)',
      '--muted':                'oklch(0.19 0.028 30)',
      '--muted-foreground':     'oklch(0.58 0.028 42)',
      '--accent':               'oklch(0.72 0.17 38)',
      '--accent-foreground':    'oklch(0.06 0.010 30)',
      '--destructive':          'oklch(0.55 0.22 15)',
      '--border':               'oklch(0.28 0.038 30)',
      '--input':                'oklch(0.22 0.030 30)',
      '--ring':                 'oklch(0.68 0.16 40)',
      '--gold':                 'oklch(0.68 0.16 40)',
      '--gold-bright':          'oklch(0.76 0.18 42)',
      '--navy':                 'oklch(0.12 0.025 30)',
      '--navy-deep':            'oklch(0.09 0.018 30)',
      '--radius':               '0.6rem',
      // ── Tokeny motywu
      '--theme-hero-opacity':   '0.75',
      '--theme-btn-glow':       'rgba(232,135,74,0.28)',
      '--theme-btn-glow-hover': 'rgba(232,135,74,0.50)',
      '--theme-accent-rgb':     '232,135,74',
      '--theme-skeleton':       '0',
      // ── Panele i dropdowny
      '--theme-panel-bg':           'oklch(0.11 0.022 28)',
      '--theme-panel-border':       'oklch(0.30 0.042 32)',
      '--theme-panel-header-bg':    'oklch(0.09 0.018 28)',
      '--theme-panel-inner-border': 'oklch(0.22 0.032 30)',
      // ── Tekst
      '--theme-section-heading':    'rgba(232,135,74,0.92)',
      '--theme-text-bright':        'rgba(248,232,210,0.96)',
      '--theme-text-muted':         'rgba(195,162,128,0.78)',
      '--theme-text-dim':           'rgba(148,118,88,0.62)',
      // ── Karty
      '--theme-card-bg-deep':       'oklch(0.12 0.022 28)',
      '--theme-card-bg-medium':     'oklch(0.17 0.028 30)',
      // ── Widget odwiedzin
      '--theme-visits-bg':          'rgba(18,8,4,0.88)',
      '--theme-visits-border':      'rgba(232,135,74,0.22)',
      '--theme-visits-accent':      'rgba(232,135,74,0.95)',
      // ── Baner
      '--theme-banner-bg':          'rgba(14,6,2,0.92)',
      '--theme-banner-border':      'rgba(232,135,74,0.30)',
      // ── Stopka
      '--theme-footer-bg':          'oklch(0.09 0.018 28)',
      '--theme-footer-card-bg':     'oklch(0.13 0.022 28)',
      '--theme-footer-card-border': 'oklch(0.26 0.036 30)',
      '--theme-footer-help-bg':     'oklch(0.11 0.020 28)',
      // ── Sekcja pomocy
      '--theme-help-color':         'rgba(100,210,155,0.82)',
      '--theme-help-bg':            'rgba(50,140,95,0.12)',
      '--theme-help-border':        'rgba(50,140,95,0.26)',
    },
  },
  {
    id: 'graphite',
    label: 'Grafitowy Cień',
    description: 'Ciemny grafit, stalowe podświetlenia, szkielety nagrobków',
    accentColor: '#7ab0d4',
    statusBarColor: '#0d0f14',
    tokens: {
      '--background':           'oklch(0.13 0.006 240)',
      '--foreground':           'oklch(0.93 0.005 210)',
      '--card':                 'oklch(0.18 0.007 235)',
      '--card-foreground':      'oklch(0.90 0.006 210)',
      '--popover':              'oklch(0.17 0.007 235)',
      '--popover-foreground':   'oklch(0.93 0.005 210)',
      '--primary':              'oklch(0.65 0.07 225)',
      '--primary-foreground':   'oklch(0.05 0.005 240)',
      '--secondary':            'oklch(0.19 0.008 232)',
      '--secondary-foreground': 'oklch(0.88 0.006 210)',
      '--muted':                'oklch(0.17 0.007 235)',
      '--muted-foreground':     'oklch(0.52 0.013 220)',
      '--accent':               'oklch(0.70 0.09 220)',
      '--accent-foreground':    'oklch(0.05 0.005 240)',
      '--destructive':          'oklch(0.55 0.18 15)',
      '--border':               'oklch(0.25 0.009 230)',
      '--input':                'oklch(0.21 0.009 232)',
      '--ring':                 'oklch(0.65 0.07 225)',
      '--gold':                 'oklch(0.65 0.07 225)',
      '--gold-bright':          'oklch(0.75 0.09 225)',
      '--navy':                 'oklch(0.09 0.012 235)',
      '--navy-deep':            'oklch(0.06 0.008 235)',
      '--theme-hero-opacity':   '0.38',
      '--theme-btn-glow':       'rgba(100,150,200,0.20)',
      '--theme-btn-glow-hover': 'rgba(100,150,200,0.38)',
      '--theme-accent-rgb':     '100,150,200',
      '--theme-skeleton':       '1',
      '--theme-panel-bg':           'oklch(0.15 0.008 235)',
      '--theme-panel-border':       'oklch(0.28 0.015 230)',
      '--theme-panel-header-bg':    'oklch(0.11 0.007 235)',
      '--theme-panel-inner-border': 'oklch(0.22 0.010 232)',
      '--theme-section-heading':    'rgba(122,176,212,0.78)',
      '--theme-text-bright':        'rgba(210,220,230,0.92)',
      '--theme-text-muted':         'rgba(140,160,185,0.72)',
      '--theme-text-dim':           'rgba(110,130,155,0.55)',
      '--theme-card-bg-deep':       'oklch(0.16 0.007 235)',
      '--theme-card-bg-medium':     'oklch(0.20 0.009 233)',
      '--theme-visits-bg':          'rgba(12,15,28,0.82)',
      '--theme-visits-border':      'rgba(100,150,200,0.18)',
      '--theme-visits-accent':      'rgba(122,176,212,0.9)',
      '--theme-banner-bg':          'rgba(12,15,28,0.90)',
      '--theme-banner-border':      'rgba(100,150,200,0.25)',
      '--theme-footer-bg':          'oklch(0.09 0.007 238)',
      '--theme-footer-card-bg':     'oklch(0.14 0.008 234)',
      '--theme-footer-card-border': 'oklch(0.24 0.012 230)',
      '--theme-footer-help-bg':     'oklch(0.12 0.007 234)',
      '--theme-help-color':         'rgba(100,205,165,0.75)',
      '--theme-help-bg':            'rgba(80,160,120,0.09)',
      '--theme-help-border':        'rgba(80,160,120,0.20)',
    },
  },
];

const STORAGE_KEY = 'egrob_theme';

/* ── Aplikuj tokeny na :root ── */
function applyTheme(theme: ThemePreset) {
  const root = document.documentElement;
  Object.entries(theme.tokens).forEach(([prop, val]) => {
    root.style.setProperty(prop, val);
  });
  // Ustaw atrybut data-theme dla CSS selektorów
  root.setAttribute('data-theme', theme.id);

  // ── Dynamicznie aktualizuj meta theme-color (pasek statusu telefonu) ──
  let metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!metaThemeColor) {
    // Stwórz tag jeśli nie istnieje (fallback)
    metaThemeColor = document.createElement('meta');
    metaThemeColor.name = 'theme-color';
    document.head.appendChild(metaThemeColor);
  }
  metaThemeColor.setAttribute('content', theme.statusBarColor);
}

/* ── Odczytaj zapisany motyw (lub classic) ── */
function loadSavedTheme(): ThemePreset {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeId | null;
    return THEMES.find(t => t.id === saved) ?? THEMES[0];
  } catch {
    return THEMES[0];
  }
}

/* ── Hook ── */
import { useState, useCallback, useEffect } from 'react';

export function useTheme() {
  const [current, setCurrent] = useState<ThemePreset>(() => loadSavedTheme());

  // Zastosuj motyw przy montowaniu i zmianie
  useEffect(() => {
    applyTheme(current);
  }, [current]);

  const setTheme = useCallback((id: ThemeId) => {
    const preset = THEMES.find(t => t.id === id) ?? THEMES[0];
    localStorage.setItem(STORAGE_KEY, id);
    applyTheme(preset);
    setCurrent(preset);
  }, []);

  return { current, setTheme, themes: THEMES };
}

/* ── Context (do użycia w Layout + przycisk ustawień) ── */
import { createContext, useContext } from 'react';

export interface ThemeContextValue {
  current: ThemePreset;
  setTheme: (id: ThemeId) => void;
  themes: ThemePreset[];
}

export const ThemeContext = createContext<ThemeContextValue>({
  current: THEMES[0],
  setTheme: () => {},
  themes: THEMES,
});

export const useThemeContext = () => useContext(ThemeContext);
